import React, { useState } from "react";
import "@/assets/css/ProductCard.css";
import { DailySpecialItem, Product } from "@/types/product";
import ProductDetails from "./ProductDetails/ProductDetails";
import comingSoonImage from "../../assets/img/comingSoon.jpeg";
import LazyImage from "@/components/ui/LazyImage";

interface DailySpecialCardProps {
  special: DailySpecialItem;
  branchId: number;
}

const DailySpecialCard: React.FC<DailySpecialCardProps> = ({
  special,
  branchId,
}) => {
  const [showProductDetails, setShowProductDetails] = useState(false);

  const handleModalOpen = () => {
    setShowProductDetails(true);
  };

  const handleModalClose = () => {
    setShowProductDetails(false);
  };

  // Convert DailySpecialItem to a Product-like object for ProductDetails
  const productFromSpecial: Product & {
    is_daily_special?: boolean;
    limit_per_order?: number | null;
    active_option_lists?: typeof special.option_lists;
  } = {
    id: special.product_id,
    name: special.product_name,
    description: special.product_description,
    image: special.product_image,
    unit_price: special.special_price,
    category: special.category_id,
    category_name: special.category_name,
    branch: branchId,
    in_stock: true,
    is_available: true,
    product_type: "Physical Product",
    is_daily_special: true,
    limit_per_order: special.limit_per_order,
    active_option_lists: special.option_lists || [],
  };

  return (
    <div
      className="md:w-full md:my-8 md:rounded-lg 
                md:shadow-[0_0_15px_rgba(0,0,0,0.2)] md:h-60 md:px-12 md:py-10 relative"
    >
      <button
        className={`w-full h-[7rem] my-2 border-b-2 border-grayCloud flex text-left
                    md:border-0 md:h-full md:my-0 justify-between`}
        onClick={handleModalOpen}
      >
        <div className={"w-[64%] h-full flex flex-col justify-between pb-5"}>
          <p className="product-name font-bold text-base md:text-xl">
            {special.product_name}
          </p>
          <div className="product-description-container">
            <p className="product-description text-xs md:text-sm font-normal min-h-[2rem]">
              {special.product_description}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Special price */}
            <p className="text-base md:text-xl font-bold text-red-600">
              ${special.special_price}
            </p>
            {/* Original price crossed out */}
            <p className="text-xs md:text-sm text-gray-400 line-through">
              ${special.original_price}
            </p>
            {/* Savings badge */}
            <span className="bg-green-100 text-green-700 text-xs font-semibold px-2 py-0.5 rounded">
              Save ${special.savings}
            </span>
          </div>
          {/* Limit per order info */}
          {special.limit_per_order && (
            <p className="text-xs text-gray-500 mt-1">
              Limit {special.limit_per_order} per order
            </p>
          )}
        </div>

        <div className="md:flex md:h-full md:items-center md:justify-center">
          <LazyImage
            src={special.product_image || comingSoonImage}
            alt={special.product_name}
            className="w-24 h-24 rounded-lg md:w-36 md:h-36 object-cover"
          />
        </div>
      </button>

      <ProductDetails
        isModalOpen={showProductDetails}
        handleModalClose={handleModalClose}
        product={productFromSpecial}
      />
    </div>
  );
};

export default DailySpecialCard;
